import { AdminPanelSettings, Person } from '@mui/icons-material';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import { Divider, Grid } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalSearchInput } from '@nx-smartmonkey/ui';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import UserSearchIcon from '../../../assets/svg/left-bar/UserSearchIcon';
import * as routing from '../../../constants/Routing';
import { useAsUser } from '../../../hooks/session/useAsUser';
import { useSearchUsers } from '../../../hooks/sideBar/useSearchUsers';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../react-components/Text';
import SideBarMenuItemContainer from './SideBarMenuItemContainer';

export const SideBarUserSearch = () => {
  const [isHover, setHover] = useState<boolean>(false);
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { asUser, deleteAsUser, beAsUser } = useAsUser();
  const [searchText, setSearchText] = useState(``);
  const { data: users } = useSearchUsers({ text: searchText });
  const [showMenu, setShowMenu] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (gridRef.current && !gridRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  useEffect(() => {
    if (showMenu) {
      document.addEventListener(`mousedown`, handleClickOutside);
    } else {
      document.removeEventListener(`mousedown`, handleClickOutside);
    }
    return () => {
      document.removeEventListener(`mousedown`, handleClickOutside);
    };
  }, [showMenu]);

  return (
    <>
      {userCredentials?.isStaff(asUser) && (
        <>
          <SideBarMenuItemContainer
            onClick={(ev: any) => {
              setShowMenu(true);
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <UserSearchIcon isHover={isHover} color={asUser && `tomato`} />
          </SideBarMenuItemContainer>
          {showMenu && (
            <Grid
              id="user-actions"
              ref={gridRef}
              style={{
                display: `flex`,
                left: `70px`,
                bottom: `80px`,
                zIndex: 452,
                position: `absolute`,
                backgroundColor: RoutalPalette.oldWhite,
                border: `1px solid ${RoutalPalette.neutral10}`,
              }}
            >
              <Grid
                container
                style={{ height: asUser ? `280px` : `240px`, width: `340px`, position: `relative` }}
                direction="column"
              >
                {asUser && (
                  <>
                    <Grid
                      sx={{
                        padding: `4px 12px`,
                        height: `38px`,
                        minHeight: `38px`,
                        display: `flex`,
                        alignItems: `center`,
                        cursor: `pointer`,
                        '&:hover': {
                          backgroundColor: RoutalPalette.neutral05,
                        },
                      }}
                      onClick={() => {
                        deleteAsUser();
                        window.open(`${window.location.origin}${routing.BACKOFFICE.CLIENTS.USERS}`, `_self`);
                      }}
                    >
                      <NoAccountsIcon />
                      <HSpacer small />
                      <Text style={{ cursor: `pointer` }}>Back to root</Text>
                    </Grid>
                    <Divider />
                  </>
                )}
                <Grid
                  container
                  wrap="nowrap"
                  item
                  direction="column"
                  style={{ height: asUser ? `calc(100% - 82px)` : `calc(100% - 44px)`, overflowY: `auto` }}
                >
                  {users?.docs.map((currentUser) => (
                    <Grid
                      key={currentUser.id}
                      sx={{
                        padding: `4px 12px`,
                        height: `38px`,
                        minHeight: `38px`,
                        display: `flex`,
                        alignItems: `center`,
                        cursor: `pointer`,
                        '&:hover': {
                          backgroundColor: currentUser.id !== user?.id ? RoutalPalette.neutral05 : undefined,
                        },
                      }}
                      onClick={() => {
                        if (currentUser.id !== user?.id) {
                          beAsUser({ id: currentUser.id });
                          window.open(routing.PLANNER.ROOT, `_self`);
                        }
                      }}
                    >
                      {currentUser.organization.is_admin ? <AdminPanelSettings /> : <Person />}
                      <HSpacer small />
                      <Text
                        style={{
                          ...(asUser === currentUser.id
                            ? { color: RoutalPalette.success.dark }
                            : { cursor: `pointer` }),
                        }}
                      >
                        {currentUser.email}
                      </Text>
                    </Grid>
                  ))}
                </Grid>
                <Grid
                  container
                  item
                  style={{ position: `absolute`, bottom: 0, height: `38px`, width: `100%`, marginBottom: `4px` }}
                  justifyContent="center"
                >
                  <RoutalSearchInput
                    inputContainerStyles={{
                      width: `100%`,
                      margin: `0 8px`,
                    }}
                    fieldValue={searchText}
                    onChange={debounce((text: string) => {
                      setSearchText(text.trim());
                    }, 300)}
                    autoFocus
                    searchLive
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </>
  );
};
